import { shuffle } from 'es-toolkit';
import { type TFunction } from 'i18next';

import { type QuizTypeOption } from '@/api/ai/useBuildAiQuiz';

/** 사용자가 설정한 상태 — 메시지 pool 필터링에 사용 */
export interface AiLoadingState {
  /** 입력 소스 타입 */
  sourceType: 'youtube' | 'file' | 'query' | 'website';
  /** 성취기준 첨부 여부 */
  hasStandard: boolean;
  /** 출제할 문제 유형 */
  quizType: QuizTypeOption;
}

interface StepMessage {
  resolve: (t: TFunction<'build'>) => string;
  /** 없으면 공통 — 모든 상태에서 pool에 포함 */
  condition?: (state: AiLoadingState) => boolean;
}

interface StepConfig {
  step: number;
  pool: StepMessage[];
  /** 이 step에서 랜덤 선택할 문구 수 (기본: 1) */
  pickCount?: number;
}

const isOx = (s: AiLoadingState) =>
  s.quizType === 'ox' || s.quizType === 'mixed';
const isMultiple = (s: AiLoadingState) =>
  s.quizType === 'multiple' || s.quizType === 'mixed';
const isShortAnswer = (s: AiLoadingState) =>
  s.quizType === 'subjective' || s.quizType === 'mixed';

export const AI_LOADING_STEPS: StepConfig[] = [
  {
    step: 1,
    pool: [
      {
        resolve: t => t('ai.loading.checkingMaterial'),
        condition: s => s.sourceType === 'query',
      },
      {
        resolve: t => t('ai.loading.loadingSubtitles'),
        condition: s => s.sourceType === 'youtube',
      },
      {
        resolve: t => t('ai.loading.readingDocument'),
        condition: s => s.sourceType === 'file',
      },
      {
        resolve: t => t('build.website.pending.scrapingMessage'),
        condition: s => s.sourceType === 'website',
      },
      {
        resolve: t => t('ai.loading.checkingStandard'),
        condition: s => s.hasStandard,
      },
    ],
  },
  {
    step: 2,
    pool: [
      { resolve: t => t('ai.loading.analyzingContent') },
      { resolve: t => t('ai.loading.identifyingConcepts') },
      { resolve: t => t('ai.loading.extractingKeyPoints') },
      { resolve: t => t('ai.loading.findingCoreLessons') },
      { resolve: t => t('ai.loading.organizingAssessment') },
    ],
  },
  {
    step: 3,
    pool: [
      { resolve: t => t('ai.loading.selectingAssessment') },
      { resolve: t => t('ai.loading.organizingConcepts') },
      {
        resolve: t => t('ai.loading.comparingStandard'),
        condition: s => s.hasStandard,
      },
      { resolve: t => t('ai.loading.reviewingGradeLevel') },
      { resolve: t => t('ai.loading.finalizingPropositions') },
    ],
  },
  {
    step: 4,
    pool: [
      { resolve: t => t('ai.loading.settingLanguage') },
      { resolve: t => t('ai.loading.applyingDifficulty') },
      { resolve: t => t('ai.loading.distributingQuestions') },
      { resolve: t => t('ai.loading.finalizingQuizType') },
      { resolve: t => t('ai.loading.adjustingFormat') },
    ],
  },
  {
    step: 5,
    pool: [
      { resolve: t => t('ai.loading.classifyingTypes') },
      {
        resolve: t => t('ai.loading.balancingRatio'),
        condition: s => isOx(s) && isMultiple(s) && isShortAnswer(s),
      },
      { resolve: t => t('ai.loading.distributingByType') },
    ],
  },
  {
    step: 6,
    pool: [
      { resolve: t => t('ai.loading.writingFirst') },
      { resolve: t => t('ai.loading.creatingOX'), condition: isOx },
      {
        resolve: t => t('ai.loading.composingMultiple'),
        condition: isMultiple,
      },
      {
        resolve: t => t('ai.loading.placingWrongAnswers'),
        condition: isMultiple,
      },
      {
        resolve: t => t('ai.loading.writingShortAnswer'),
        condition: isShortAnswer,
      },
      { resolve: t => t('ai.loading.verifyingAnswers') },
      { resolve: t => t('ai.loading.polishingForStudents') },
      {
        resolve: t => t('ai.loading.aligningStandard'),
        condition: s => s.hasStandard,
      },
      { resolve: t => t('ai.loading.writingNext') },
      { resolve: t => t('ai.loading.balancingDifficulty') },
    ],
  },
  {
    step: 7,
    pool: [
      { resolve: t => t('ai.loading.compilingQuestions') },
      { resolve: t => t('ai.loading.checkingDuplicates') },
      { resolve: t => t('ai.loading.orderingQuestions') },
      { resolve: t => t('ai.loading.finalCheckAnswers') },
      { resolve: t => t('ai.loading.qualityCheck') },
      { resolve: t => t('ai.loading.finishing') },
    ],
  },
];

export const AI_LOADING_SLIDE_INTERVAL_MS = 2000;

export interface LoadingMessage {
  text: string;
  step: number;
}

function pickRandom<T>(items: T[], count: number): T[] {
  return shuffle(items).slice(0, count);
}

/**
 * 사용자 상태 기반으로 keen-slider에 전달할 flat 메시지 배열 생성.
 * 각 Step에서 조건 통과 pool → 랜덤 1개 (pool이 없으면 0개).
 */
export function buildLoadingMessages(
  state: AiLoadingState,
  t: TFunction<'build'>,
  steps: StepConfig[] = AI_LOADING_STEPS,
): LoadingMessage[] {
  return steps.flatMap(({ step, pool, pickCount = 1 }) => {
    const eligible = pool.filter(m => m.condition?.(state) ?? true);
    return pickRandom(eligible, pickCount).map(m => ({
      text: m.resolve(t),
      step,
    }));
  });
}
